/**
 * Device-local cache of preloaded videos, so reopening a video the narrator
 * already downloaded plays instantly instead of downloading all over again.
 *
 * Kept in its own IndexedDB (separate from `recordingsCache`, which holds
 * irreplaceable recordings) so this bounded, self-evicting cache can be wiped
 * wholesale without touching anything that matters. Bounded to MAX_CACHE_BYTES
 * with least-recently-used eviction.
 *
 * Every function resolves rather than rejects on failure: a cache miss or a
 * storage error must never break playback — the caller always still holds the
 * blob in memory and can fall back to streaming.
 */

const DB_NAME = 'videoCache';
const DB_VERSION = 1;
const BLOB_STORE = 'videoBlobs';   // keyPath 'videoId' -> { videoId, blob }
const META_STORE = 'videoMeta';    // keyPath 'videoId' -> { videoId, s3Path, size, lastUsedAt }

// Total on-disk budget for cached videos. Narrators record on Chrome, whose
// per-origin quota is far larger than this; the 5 GB cap is our own policy so a
// handful of ~1 GB videos can't fill the disk. Evict LRU to stay under it.
export const MAX_CACHE_BYTES = 5 * 1024 * 1024 * 1024;

// Monotonic "last used" stamp. Date.now() alone has millisecond granularity, so
// two accesses in the same tick would tie and evict unpredictably; this makes
// every access strictly newer than the last within a session while staying
// time-based (and thus correctly ordered) across sessions.
let lastStamp = 0;
const nextStamp = () => {
  const now = Date.now();
  lastStamp = now > lastStamp ? now : lastStamp + 1;
  return lastStamp;
};

const initDB = () =>
  new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(BLOB_STORE)) {
        db.createObjectStore(BLOB_STORE, { keyPath: 'videoId' });
      }
      if (!db.objectStoreNames.contains(META_STORE)) {
        db.createObjectStore(META_STORE, { keyPath: 'videoId' });
      }
    };
    request.onsuccess = (event) => resolve(event.target.result);
    request.onerror = (event) => reject(event.target.error);
  });

const promisifyTx = (tx) =>
  new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve();
    tx.onabort = () => reject(tx.error);
    tx.onerror = () => reject(tx.error);
  });

const promisifyReq = (req) =>
  new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });

const readAllMeta = async (db) => {
  const tx = db.transaction([META_STORE], 'readonly');
  return promisifyReq(tx.objectStore(META_STORE).getAll());
};

const deleteEntry = async (db, videoId) => {
  const tx = db.transaction([BLOB_STORE, META_STORE], 'readwrite');
  tx.objectStore(BLOB_STORE).delete(videoId);
  tx.objectStore(META_STORE).delete(videoId);
  await promisifyTx(tx);
};

/**
 * Return the cached blob for a video if present and still current, else null.
 * A stored entry whose S3 object path no longer matches (the admin swapped the
 * video's file) is treated as a miss and purged. On a hit, bumps lastUsedAt.
 *
 * @param {number|string} videoId
 * @param {string} s3Path - pathname of the current presigned URL (no query)
 * @returns {Promise<Blob|null>}
 */
export const getCachedVideo = async (videoId, s3Path) => {
  const key = String(videoId);
  try {
    const db = await initDB();
    const metaTx = db.transaction([META_STORE], 'readonly');
    const meta = await promisifyReq(metaTx.objectStore(META_STORE).get(key));
    if (!meta || meta.s3Path !== s3Path) {
      if (meta) await deleteEntry(db, key); // stale: the underlying file changed
      return null;
    }
    const blobTx = db.transaction([BLOB_STORE], 'readonly');
    const record = await promisifyReq(blobTx.objectStore(BLOB_STORE).get(key));
    if (!record || !record.blob) {
      await deleteEntry(db, key);
      return null;
    }
    // Best-effort LRU bump; don't fail the read if it can't be written.
    try {
      const touchTx = db.transaction([META_STORE], 'readwrite');
      touchTx.objectStore(META_STORE).put({ ...meta, lastUsedAt: nextStamp() });
      await promisifyTx(touchTx);
    } catch (_) { /* ignore */ }
    return record.blob;
  } catch (err) {
    console.warn('videoCache: read failed, treating as miss:', err?.message || err);
    return null;
  }
};

const totalSize = (metas) => metas.reduce((sum, m) => sum + (m.size || 0), 0);

/**
 * Store a video blob, evicting least-recently-used entries to stay within the
 * byte budget. Never throws — on any failure it resolves without caching, since
 * the caller already holds the blob.
 *
 * @param {number|string} videoId
 * @param {string} s3Path
 * @param {Blob} blob
 * @param {{maxBytes?: number}} [opts] - maxBytes override (for tests)
 */
export const putCachedVideo = async (videoId, s3Path, blob, opts = {}) => {
  const key = String(videoId);
  const maxBytes = opts.maxBytes ?? MAX_CACHE_BYTES;
  if (!blob || blob.size > maxBytes) return; // a single video too big to ever fit

  try {
    const db = await initDB();

    // Evict LRU (excluding this key, which we're about to overwrite) until the
    // new blob fits within budget.
    let metas = (await readAllMeta(db)).filter((m) => m.videoId !== key);
    metas.sort((a, b) => (a.lastUsedAt || 0) - (b.lastUsedAt || 0));
    while (metas.length && totalSize(metas) + blob.size > maxBytes) {
      const victim = metas.shift();
      await deleteEntry(db, victim.videoId);
    }

    await writeEntry(db, key, s3Path, blob);
  } catch (err) {
    // Quota is the expected failure: evict the oldest and retry until it fits or
    // the cache is empty. Give up silently if it still won't take.
    if (err && err.name === 'QuotaExceededError') {
      try {
        const db = await initDB();
        let metas = (await readAllMeta(db)).filter((m) => m.videoId !== key);
        metas.sort((a, b) => (a.lastUsedAt || 0) - (b.lastUsedAt || 0));
        while (metas.length) {
          const victim = metas.shift();
          await deleteEntry(db, victim.videoId);
          try {
            await writeEntry(db, key, s3Path, blob);
            return;
          } catch (retryErr) {
            if (retryErr?.name !== 'QuotaExceededError') throw retryErr;
          }
        }
      } catch (_) { /* give up: play from memory, uncached */ }
    }
    console.warn('videoCache: write skipped:', err?.message || err);
  }
};

const writeEntry = async (db, key, s3Path, blob) => {
  const tx = db.transaction([BLOB_STORE, META_STORE], 'readwrite');
  tx.objectStore(BLOB_STORE).put({ videoId: key, blob });
  tx.objectStore(META_STORE).put({ videoId: key, s3Path, size: blob.size, lastUsedAt: nextStamp() });
  await promisifyTx(tx);
};

/** Remove a single cached video (used when the caller knows it is stale). */
export const deleteCachedVideo = async (videoId) => {
  try {
    const db = await initDB();
    await deleteEntry(db, String(videoId));
  } catch (err) {
    console.warn('videoCache: delete failed:', err?.message || err);
  }
};
